import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatDateWithClock } from '~/services/TimeService';

import { getLatestSyncedAt } from '../../../CMChecklist/shared/checklistUtils';
import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';
import { UNMATCHED_ROW_ID, salesforceTableHeadSx, toggleExpandedRow } from '../surcharges/surchargeTabLayout';

import AppliedEgsTable from './AppliedEgsTable';
import EgsRoomRow from './EgsRoomRow';
import EgsSectionHeading from './EgsSectionHeading';
import EgsUnmatchedSection from './EgsUnmatchedSection';
import { buildAppliedEgsGroups } from './appliedEgsRows';
import { buildEgsMatchByRoomLabel, getRoomRateIds, getUnmatchedRoomRateIds } from './egsRoomMatcher';
import { countRoomRatesWithEgs, groupEgsByRoom } from './egsUtils';
import type { EgsTabProps } from './types';

const EGS_TABLE_COLUMN_COUNT = 6;

export default function EgsTab({
  error,
  extraGuestSurchargesByRoomRate,
  isLoading,
  isLoadingRoomRateEgs,
  isLoadingRoomTypes,
  roomRateEgsError,
  roomTypes,
  roomTypesError,
  surcharges,
}: EgsTabProps) {
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(new Set());
  const roomGroups = useMemo(() => groupEgsByRoom(surcharges), [surcharges]);
  const matchByRoomLabel = useMemo(
    () =>
      buildEgsMatchByRoomLabel(
        roomGroups.map((group) => group.roomLabel),
        roomTypes,
      ),
    [roomGroups, roomTypes],
  );
  const unmatchedRoomRateIds = useMemo(
    () =>
      getUnmatchedRoomRateIds({
        egsByRoomRateId: extraGuestSurchargesByRoomRate,
        matchByRoomLabel,
      }),
    [extraGuestSurchargesByRoomRate, matchByRoomLabel],
  );
  const roomRatesWithEgs = useMemo(
    () => countRoomRatesWithEgs(extraGuestSurchargesByRoomRate),
    [extraGuestSurchargesByRoomRate],
  );
  const appliedGroups = useMemo(
    () => buildAppliedEgsGroups({ egsByRoomRateId: extraGuestSurchargesByRoomRate, roomTypes }),
    [extraGuestSurchargesByRoomRate, roomTypes],
  );
  const totalRoomRates = useMemo(() => getRoomRateIds(roomTypes).length, [roomTypes]);
  const lastSyncedAt = useMemo(
    () => getLatestSyncedAt(surcharges.map((surcharge) => surcharge.synced_at)),
    [surcharges],
  );
  useDealInformationTabCount('egs', isLoading || error ? undefined : roomGroups.length);

  const hasRoomTypes = roomTypes.length > 0;
  const hasNoRoomRates = !isLoadingRoomTypes && !roomTypesError && !hasRoomTypes;

  const salesforceFacts = [
    `${roomGroups.length} room${roomGroups.length === 1 ? '' : 's'}`,
    lastSyncedAt ? `last synced ${formatDateWithClock(lastSyncedAt)}` : undefined,
  ].filter(Boolean);
  const roomRateFacts = [
    hasNoRoomRates ? 'none on this offer' : undefined,
    roomRateEgsError ? 'unavailable' : undefined,
    isLoadingRoomRateEgs || roomRateEgsError || hasNoRoomRates
      ? undefined
      : `${roomRatesWithEgs} of ${totalRoomRates} with surcharges`,
  ].filter(Boolean);
  const subtitle = [
    salesforceFacts.length ? `Salesforce: ${salesforceFacts.join(', ')}` : undefined,
    roomRateFacts.length ? `Room rates: ${roomRateFacts.join(', ')}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  const toggleRoom = useCallback(
    (roomLabel: string) => setExpandedRooms((current) => toggleExpandedRow(current, roomLabel)),
    [],
  );
  const toggleUnmatched = useCallback(() => toggleRoom(UNMATCHED_ROW_ID), [toggleRoom]);

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading extra guest surcharges.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load Salesforce extra guest surcharges.</Alert>;
  }

  return (
    <TabPanelShell title="Extra Guest Surcharges" subtitle={subtitle} contentSx={{ p: 0 }}>
      <AppliedEgsTable error={roomRateEgsError} groups={appliedGroups} isLoading={isLoadingRoomRateEgs} />

      <EgsSectionHeading
        description="Salesforce names each surcharge after the room it applies to, with one row per guest type. Expand a room to see its amounts and whether it matched a room rate on this offer."
        title="Salesforce EGS"
      />
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            {/* Blue for Salesforce data, grey on the applied table above for our own. */}
            <TableRow sx={salesforceTableHeadSx}>
              <TableCell />
              <TableCell>Room</TableCell>
              <TableCell>Guest types</TableCell>
              <TableCell align="center">Rows</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Room rates</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roomGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={EGS_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce extra guest surcharges found for this opportunity.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {roomGroups.map((group) => (
              <EgsRoomRow
                key={group.roomLabel}
                columnCount={EGS_TABLE_COLUMN_COUNT}
                group={group}
                hasRoomTypes={hasRoomTypes}
                isExpanded={expandedRooms.has(group.roomLabel)}
                isLoadingRoomTypes={isLoadingRoomTypes}
                match={matchByRoomLabel[group.roomLabel]}
                roomTypes={roomTypes}
                roomTypesError={roomTypesError}
                onToggleExpanded={toggleRoom}
              />
            ))}
            <EgsUnmatchedSection
              columnCount={EGS_TABLE_COLUMN_COUNT}
              error={roomRateEgsError}
              isExpanded={expandedRooms.has(UNMATCHED_ROW_ID)}
              isLoading={isLoadingRoomRateEgs}
              roomRateIds={unmatchedRoomRateIds}
              roomTypes={roomTypes}
              onToggleExpanded={toggleUnmatched}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
